import React, { useEffect, useRef } from 'react';

import { Field, FormikErrors, FormikValues } from 'formik';

import moment from 'moment';

import VehicleIcon from '@mui/icons-material/LocalShipping';
import EmployeeIcon from '@mui/icons-material/Person';
import { Box } from '@mui/material';

import TimeFormatter from '../../../helpers/TimeFormatter';
import TranslationHelper from '../../../helpers/TranslationHelper';
import { getTypesIds } from '../utils';

import { IPrivileges } from '../../../state/auth';
import { ITaskForm } from '../../../state/ui/forms';
import { IDictionaryItem } from '../../../state/app/dictionaries';
import useSearchersLogic from '../../../state/app/searchers/index.hooks';

import { areLocationsEnabled } from '../../../constants/AppConfig';
import { DONE } from '../../../constants/dictionaries/TaskStatuses';

import DateFieldWrapper from '../../../components/common/formikWrappers/DateFieldWrapper';
import FieldWrapper from '../../../components/common/formikWrappers/FieldWrapper';
import FileUploadWrapper from '../../../components/common/formikWrappers/FileUploadWrapper';
import GridSearchSelectWrapper from '../../../components/common/formikWrappers/GridSearchSelectWrapper';
import PhraseFilterWrapper from '../../../components/common/formikWrappers/PhraseFilterWrapper';
import CustomCheckbox from '../../../components/common/CustomCheckbox';
import TimeRangeFormFields from '../../../components/TimeRangeDisplay/TimeRangeFormFields';
import { showTaskRoutesDialog } from '../../../state/ui/dialog';
import { useAppDispatch } from '../../../hooks';
import {
    mapDictionaryItemToMenuItem,
    renderMenuItems,
} from '../../../helpers/renderMenuItems';
import ExternalLinkages from '../../../components/ExternalLinkages';
import { useStyles } from '../Themable.hooks';

interface IOwnProps {
    activityCategory: IDictionaryItem[];
    objectCategory: IDictionaryItem[];
    serviceClass: IDictionaryItem[];
    serviceType: IDictionaryItem[];
    statuses: IDictionaryItem[];
    privileges: IPrivileges;
    onSelectLocation: () => void;
    onAddLocation?: () => void;
    values: FormikValues;
    errors?: FormikErrors<ITaskForm>;
    setFieldValue: (name: string, value: any) => void;
    dateFormat: string;
    mode: 'add' | 'edit' | 'copy';
    routeAssigned: boolean | null;
}

const TaskFormFields = ({
    errors,
    values,
    onSelectLocation,
    onAddLocation,
    activityCategory,
    serviceClass,
    serviceType,
    objectCategory,
    statuses,
    dateFormat,
    setFieldValue,
    mode,
    privileges,
    routeAssigned,
}: IOwnProps) => {
    const classes = useStyles();
    const checkboxDisabled = values.status === DONE || routeAssigned;

    const initialRender = useRef(true);
    const dispatch = useAppDispatch();

    const {
        onSearchRegister,
        areSearchResultsVisible,
        getRegisterResults,
        areSearchResultsFetching,
        onResetRegister,
    } = useSearchersLogic();

    useEffect(() => {
        if (
            (values.route?.id && !initialRender.current) ||
            (initialRender.current && values.noDate)
        ) {
            setFieldValue('data.route', { label: '' });
        }
    }, [values.noDate, values.plannedOn, values.activityCategory]);
    useEffect(() => {
        if (!initialRender.current) {
            const { serviceTypeId, objectCategoryId } = getTypesIds(
                values.activityCategory,
                serviceType,
                objectCategory
            );
            setFieldValue('data.serviceType', serviceTypeId);
            setFieldValue('data.objectCategory', objectCategoryId);
            setFieldValue('data.serviceClass', '');
        } else {
            initialRender.current = false;
        }
    }, [values.activityCategory]);

    useEffect(() => {
        if (values.status === DONE) {
            if (!values.plannedOn && mode !== 'copy') {
                setFieldValue('data.plannedOn', today);
            }
            setFieldValue('noDate', false);
        }
    }, [values.status]);

    useEffect(() => {
        if (
            values.noDate === false &&
            values.plannedOn === null &&
            mode !== 'copy'
        ) {
            setFieldValue('data.plannedOn', today);
        }
    }, [values.noDate]);

    const today = moment().format('YYYY-MM-DD');
    //@ts-ignore
    const locationError = errors?.['location.id'];
    const hasRightToLocations = areLocationsEnabled() && privileges.locations;

    const missingDropdownData = [
        activityCategory,
        serviceType,
        objectCategory,
        statuses,
    ].some((el) => el?.length < 1);
    const disabledRoute =
        values.noDate ||
        !values.activityCategory ||
        (!values.employee.id && !values.vehicle.id) ||
        routeAssigned ||
        routeAssigned === false;
    return (
        <div>
            {/*TODO this is only temporary solution - it appears in more places in application, so it probably needs more universal approach*/}
            {missingDropdownData ? (
                <h1 className={classes.missingDataWrapper}>
                    {TranslationHelper.translate(
                        'Missing configuration of services required to create new task.'
                    )}
                </h1>
            ) : (
                <>
                    <div className={classes.paneContent}>
                        <Box display="flex">
                            <Field
                                error={!!(errors && errors.plannedOn)}
                                helperText={errors && errors.plannedOn}
                                name={'data.plannedOn'}
                                label={TranslationHelper.translate('Date')}
                                component={DateFieldWrapper}
                                required={true}
                                disabled={values.noDate || routeAssigned}
                                displayFormat={dateFormat}
                                defaultDate={mode === 'copy' ? null : undefined}
                                value={values.noDate ? null : values.plannedOn}
                                withDateFormatter={
                                    TimeFormatter.dateToShortDateString
                                }
                            />
                            <Field
                                name={'data.noDate'}
                                label={TranslationHelper.translate(
                                    'Without date'
                                )}
                                checked={values?.noDate}
                                onChange={() =>
                                    setFieldValue(
                                        'data.noDate',
                                        !values?.noDate
                                    )
                                }
                                component={CustomCheckbox}
                                useContrastColors={false}
                                disabled={checkboxDisabled}
                            />
                        </Box>
                        <TimeRangeFormFields
                            timeFrom={{
                                value: values.plannedTimeFrom,
                                errors: errors?.plannedTimeFrom,
                                name: 'data.plannedTimeFrom',
                            }}
                            timeTo={{
                                value: values.plannedTimeTo,
                                errors: errors?.plannedTimeTo,
                                name: 'data.plannedTimeTo',
                            }}
                        />

                        <Field
                            error={!!locationError}
                            helperText={locationError}
                            name={'data.location.label'}
                            label={TranslationHelper.translate('Address')}
                            type={'text'}
                            component={GridSearchSelectWrapper}
                            required={true}
                            InputProps={{ readOnly: true }}
                            onClick={
                                hasRightToLocations
                                    ? onSelectLocation
                                    : undefined
                            }
                            onAdd={
                                hasRightToLocations ? onAddLocation : undefined
                            }
                        />

                        <Field
                            name={'data.vehicle'}
                            placeholder={TranslationHelper.translate(
                                'Find by name'
                            )}
                            fetchDataHandler={(name: string) =>
                                onSearchRegister(name, 'vehicles')
                            }
                            visible={areSearchResultsVisible('vehicles')}
                            fetching={areSearchResultsFetching('vehicles')}
                            results={getRegisterResults('vehicles')}
                            afterSelect={() => {
                                if (routeAssigned === null) {
                                    setFieldValue('data.route', { label: '' });
                                }
                            }}
                            onClose={() => {
                                onResetRegister('vehicles');
                            }}
                            component={PhraseFilterWrapper}
                            icon={<VehicleIcon />}
                            disabled={routeAssigned}
                        />
                        <Field
                            name={'data.employee'}
                            placeholder={TranslationHelper.translate(
                                'Find by name'
                            )}
                            visible={areSearchResultsVisible('employees')}
                            fetching={areSearchResultsFetching('employees')}
                            results={getRegisterResults('employees')}
                            afterSelect={() => {
                                if (routeAssigned === null) {
                                    setFieldValue('data.route', { label: '' });
                                }
                            }}
                            onClose={() => {
                                onResetRegister('employees');
                            }}
                            fetchDataHandler={(name: string) =>
                                onSearchRegister(name, 'employees')
                            }
                            component={PhraseFilterWrapper}
                            icon={<EmployeeIcon />}
                            disabled={routeAssigned}
                        />

                        <Field
                            name={'data.route.label'}
                            placeholder={TranslationHelper.translate('Route')}
                            component={GridSearchSelectWrapper}
                            InputProps={{ readOnly: true }}
                            label={TranslationHelper.translate('Route')}
                            disabled={disabledRoute}
                            onClick={() =>
                                values.activityCategory &&
                                (values.employee.id || values.vehicle.id) &&
                                dispatch(
                                    showTaskRoutesDialog({
                                        filter: {
                                            activityCategoryId:
                                                values.activityCategory,
                                            employeeId: values.employee.id,
                                            vehicleId: values.vehicle.id,
                                            date: values.plannedOn,
                                        },
                                        routeId: values.route.id,
                                        setFieldValue: setFieldValue,
                                    })
                                )
                            }
                        />

                        <Field
                            error={!!(errors && errors.status)}
                            helperText={errors && errors.status}
                            name={'data.status'}
                            label={TranslationHelper.translate('Status')}
                            select={true}
                            fullWidth={true}
                            afterOnChange={(value: number) => {
                                if (value === DONE) {
                                    setFieldValue('data.noDate', false);
                                }
                            }}
                            component={FieldWrapper}
                            required={true}
                        >
                            {renderMenuItems(statuses, (item) => item)}
                        </Field>
                        <Field
                            error={!!(errors && errors.activityCategory)}
                            helperText={errors && errors.activityCategory}
                            name={'data.activityCategory'}
                            label={TranslationHelper.translate('Type 1')}
                            select={true}
                            fullWidth={true}
                            component={FieldWrapper}
                            required={true}
                            disabled={routeAssigned !== null}
                        >
                            {renderMenuItems(activityCategory, (item) => item)}
                        </Field>
                        <Field
                            error={!!(errors && errors.serviceType)}
                            helperText={errors && errors.serviceType}
                            name={'data.serviceType'}
                            label={TranslationHelper.translate('Type 2')}
                            select={true}
                            fullWidth={true}
                            component={FieldWrapper}
                            disabled={!values.activityCategory}
                            required={true}
                        >
                            {renderMenuItems(
                                serviceType,
                                (item) => item,
                                values.activityCategory
                            )}
                        </Field>
                        <Field
                            error={!!(errors && errors.objectCategory)}
                            helperText={errors && errors.objectCategory}
                            name={'data.objectCategory'}
                            label={TranslationHelper.translate('Type 3')}
                            select={true}
                            fullWidth={true}
                            component={FieldWrapper}
                            disabled={!values.activityCategory}
                            required={true}
                        >
                            {renderMenuItems(
                                objectCategory,
                                (item) => item,
                                values.activityCategory
                            )}
                        </Field>
                        <Field
                            name={'data.serviceClass'}
                            label={TranslationHelper.translate('Type 4')}
                            select={true}
                            fullWidth={true}
                            disabled={!values.activityCategory}
                            component={FieldWrapper}
                        >
                            {renderMenuItems(
                                serviceClass,
                                mapDictionaryItemToMenuItem,
                                values.activityCategory,
                                true
                            )}
                        </Field>
                        <Field
                            error={!!(errors && errors.unitsCount)}
                            helperText={errors && errors.unitsCount}
                            name={'data.unitsCount'}
                            label={TranslationHelper.translate('Units count')}
                            type={'number'}
                            fullWidth={true}
                            component={FieldWrapper}
                            inputProps={{
                                min: '0',
                            }}
                            required={true}
                        />
                        <Field
                            error={!!(errors && errors.notice)}
                            helperText={errors && errors.notice}
                            name={'data.notice'}
                            label={TranslationHelper.translate('Notice')}
                            type={'text'}
                            fullWidth={true}
                            multiline={true}
                            component={FieldWrapper}
                        />
                    </div>
                    <div className={classes.attachmentsPane}>
                        <Field
                            error={
                                values &&
                                values.attachments &&
                                values.attachments.errors
                            }
                            name={'data.attachments'}
                            component={FileUploadWrapper}
                            readOnlyFiles={values.readOnlyFiles}
                            maxSize={20}
                        />
                    </div>
                    {mode === 'add' && privileges.xtrackServiceSystem && (
                        <ExternalLinkages
                            values={values.externalLinkages}
                            errors={
                                errors?.externalLinkages as unknown as Array<{
                                    appName: string;
                                    externalId: string;
                                }>
                            }
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default TaskFormFields;
